import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { CHART_VIEW_MODES } from '@aytm/app/stats/page/question-views/oe/lib/chartsModel'

import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'

import { OE_FAMILY_LIB } from '@aytm/app/stats/page/question-views/oe/lib/family'
import useOeBlock from '@aytm/app/stats/page/question-views/oe/states/useOeBlock'

import Controls from '@aytm/app/stats/page/question-views/oe/components/Controls'

import ChartsView from '@aytm/app/stats/page/question-views/oe/components/views/ChartsView'
import VerbatimView from '@aytm/app/stats/page/question-views/oe/components/views/VerbatimView'
import WordCloudView from '@aytm/app/stats/page/question-views/oe/components/views/WordCloudView'

import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

// The open-ended layout: which of the four view modes is on screen, and the controls strip
// above them. Every view computes what it draws from the bag it is handed -- nothing here
// reads page state or touches report params.
export const BlockView = ({
  viewMode,
  components,
  chartHeight,
  controlsProps,
  verbatimProps,
  chartProps,
  tableProps,
  collapsed = false,
  missingTotals = false,
  answer,
  wcProps,
  chartsModelProps
}) => {
  // totals are still being computed page-wide: no controls, just a held-open frame
  if (missingTotals) return <ChartPlaceholder isLoading />

  return (
    <>
      {isComponentOn(components, 'viewModes') && <Controls {...controlsProps} />}
      {!collapsed && (
        <>
          {CHART_VIEW_MODES.includes(viewMode) && (
            <ChartsView
              components={components}
              answer={answer}
              chartHeight={chartHeight}
              chartsModelProps={chartsModelProps}
              chartProps={chartProps}
              tableProps={tableProps}
            />
          )}
          {'verbatim' === viewMode && <VerbatimView components={components} {...verbatimProps} />}
          {'wordcloud' === viewMode && <WordCloudView {...wcProps} />}
        </>
      )}
    </>
  )
}

BlockView.displayName = 'OeBlockView'

BlockView.propTypes = {
  viewMode: PropTypes.string.isRequired,
  components: PropTypes.object,
  chartHeight: PropTypes.number,
  answer: answerPropType,
  missingTotals: PropTypes.bool,
  collapsed: PropTypes.bool,
  controlsProps: PropTypes.object,
  verbatimProps: PropTypes.object,
  chartProps: PropTypes.object,
  tableProps: PropTypes.object,
  wcProps: PropTypes.object,
  chartsModelProps: PropTypes.object
}

// The open-ended block. It owns how the answers are being looked at and every bag the layout
// below draws from; block.coffee owns the mount, the report-param write and the payload, and
// takes the state back through the api this hands up.
// The layout with its state resolved, and nothing around it. A question drawing an open-ended cloud
// as part of its own body -- a matrix batch, an oex answer variant, the summary cloud a scale or
// ranking question offers -- reaches for this: the question chrome is the host's, drawn once around
// everything it holds, so a cloud inside it must not bring a second one.
export const BlockContent = props => (
  <BlockView {...useOeBlock({ ...props, lib: OE_FAMILY_LIB })} chartHeight={props.chartHeight} />
)

BlockContent.displayName = 'OeBlockContent'

// The open-ended question in its own right, chrome and all.
const Block = props => (
  <BlockWrapper {...props}>
    <BlockContent {...props} />
  </BlockWrapper>
)

Block.displayName = 'OeBlock'

// What the shell hands in: the question, the sections of the report params the block owns, and
// the handlers the surfaces report back through.
export const BLOCK_PROP_TYPES = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // false where the block is drawn inside another question, which reports the count itself
  questionKey: PropTypes.string,
  type: PropTypes.string,
  variant: PropTypes.string,
  format: PropTypes.object,
  verbatimFormat: PropTypes.object,
  components: PropTypes.object,
  answer: answerPropType,
  answerVariants: PropTypes.array,
  dimensions: PropTypes.object,
  disabled: PropTypes.bool,
  clickable: PropTypes.bool,
  questionChartMode: PropTypes.string,
  modelDefaults: PropTypes.object,
  rejects: PropTypes.object,
  chartHeight: PropTypes.number,
  params: PropTypes.shape({
    oe: PropTypes.object,
    cm: PropTypes.string,
    z: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    s: PropTypes.string,
    wc: PropTypes.string,
    qs: PropTypes.string,
    mncc: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
  }),
  // bumped by the shell on every host render, so a re-read of an unchanged section still registers
  paramsRevision: PropTypes.number,
  chartViewModes: PropTypes.arrayOf(PropTypes.string),
  chartModeViewModes: PropTypes.arrayOf(PropTypes.string),
  defaultSortOrder: PropTypes.string,
  healViewMode: PropTypes.bool,
  labelTarget: PropTypes.object,
  commentsTarget: PropTypes.object,
  handlers: PropTypes.object,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func,
  onWcParamsChange: PropTypes.func,
  onOutdatedPreset: PropTypes.func
}

Block.propTypes = BLOCK_PROP_TYPES

export default Block
